import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";

import {
    applyAddElements,
    applyFlipElements,
    applySetPartVersionDataCache,
    applyUpdateElementProperties,
    applyUpdateElementsSchematicPosition,
} from "../actions/elementActions";
import {performDocumentMutation} from "../documentMutations";
import type {DocumentStoreState} from "../types";

import type {SliceDependencies} from "./types";

type ElementSliceState = Pick<
    DocumentStoreState,
    | "addElements"
    | "updateElementsSchematicPosition"
    | "updateElementProperties"
    | "flipElements"
    | "setPartVersionDataCache"
    | "setHeadPartVersionDataCache"
>;

export function createElementSlice(deps: SliceDependencies): ElementSliceState {
    const {primitives, get} = deps;

    return {
        addElements: (params, inPcbEditor) => {
            const {elements} = params;
            if (!elements || elements.length === 0) {
                return;
            }
            const document = get().document;
            if (!document?.elements || !document?.uid || !document?.pcbLayoutNodes) {
                return;
            }
            try {
                performDocumentMutation(primitives, (draft) => applyAddElements(draft, {...params, inPcbEditor}), {
                    actionType: "addElements",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                });
            } catch (error) {
                FluxLogger.captureError(error);
            }
        },

        updateElementsSchematicPosition: (elements, vertexUpdates) => {
            const hasElements = elements && elements.length > 0;
            const hasVertexUpdates = vertexUpdates && vertexUpdates.length > 0;
            if (!hasElements && !hasVertexUpdates) {
                return;
            }
            const document = get().document;
            if (!document?.elements || !document?.pcbLayoutNodes) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) => applyUpdateElementsSchematicPosition(draft, elements, vertexUpdates),
                {
                    actionType: "updateElementsSchematicPosition",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                },
            );
        },

        updateElementProperties: (elementUid, properties) => {
            if (!elementUid || !properties) {
                return;
            }
            const document = get().document;
            if (!document?.elements?.[elementUid]) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) => applyUpdateElementProperties(draft, elementUid, properties),
                {
                    actionType: "updateElementProperties",
                    canUndo: false,
                    shouldGenerateActionRecord: false,
                    updatesDocumentTimestamp: false,
                },
            );
        },

        flipElements: (elements, routeVertexUpdates) => {
            if (!elements || elements.length === 0) {
                return;
            }
            const document = get().document;
            if (!document?.elements) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applyFlipElements(draft, elements, routeVertexUpdates), {
                actionType: "flipElements",
                canUndo: true,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
            });
        },

        setPartVersionDataCache: (partUid, partVersionData) => {
            if (!partUid || !partVersionData) {
                return;
            }
            const document = get().document;
            if (!document?.elements || !document?.uid || !document?.pcbLayoutNodes) {
                return;
            }
            const elementsToChange = Object.values(document.elements).filter(
                (elementData) =>
                    elementData.part_uid === partUid &&
                    elementData.part_version_data_cache.version !== partVersionData.version,
            );
            if (!elementsToChange.length) {
                return;
            }
            try {
                performDocumentMutation(
                    primitives,
                    (draft) => applySetPartVersionDataCache(draft, elementsToChange, partVersionData),
                    {
                        actionType: "setPartVersionDataCache",
                        canUndo: false,
                        shouldGenerateActionRecord: true,
                        updatesDocumentTimestamp: true,
                        additionalPayload: {
                            hasInheritedRules: true,
                            hasInheritedCalculations: true,
                            requiresDescendantRebake: true,
                            requiresNetDescendantRebake: true,
                        },
                    },
                );
            } catch (error) {
                FluxLogger.captureError(error);
            }
        },

        setHeadPartVersionDataCache: (partUid, partVersionData) => {
            if (!partUid || !partVersionData) {
                return;
            }
            const document = get().document;
            if (!document?.elements || !document?.uid || !document?.pcbLayoutNodes) {
                return;
            }
            const elementsToChange = Object.values(document.elements).filter(
                (elementData) =>
                    elementData.part_uid === partUid &&
                    elementData.part_version_data_cache.version !== partVersionData.version,
            );
            if (!elementsToChange.length) {
                return;
            }
            try {
                performDocumentMutation(
                    primitives,
                    (draft) => applySetPartVersionDataCache(draft, elementsToChange, partVersionData),
                    {
                        actionType: "setHeadPartVersionDataCache",
                        canUndo: false,
                        shouldGenerateActionRecord: false,
                        updatesDocumentTimestamp: false,
                        additionalPayload: {
                            hasInheritedRules: true,
                            hasInheritedCalculations: true,
                            requiresDescendantRebake: true,
                            requiresNetDescendantRebake: true,
                        },
                    },
                );
            } catch (error) {
                FluxLogger.captureError(error);
            }
        },
    };
}
